import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, XCircle, Clock, Eye, DollarSign, AlertTriangle,
  Building, Calendar, CreditCard, FileText, User
} from 'lucide-react';
import { Card, Button, Select, Textarea, Table, Modal, Badge, ConfirmDialog, Loading } from '../components/ui';
import { pagosPropietarioService } from '../services/api';
import { formatMoney, formatDate } from '../utils/formatters';
import toast from 'react-hot-toast';

const ConfirmarPagosPage = () => {
  const [loading, setLoading] = useState(true);
  const [pagos, setPagos] = useState([]);
  const [pendientesCount, setPendientesCount] = useState(0);
  const [filters, setFilters] = useState({ estado: 'PENDIENTE' });
  const [modalDetalle, setModalDetalle] = useState(false);
  const [modalRechazar, setModalRechazar] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selected, setSelected] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await pagosPropietarioService.getAll(filters);
      setPagos(res.data.data.pagos || []);
      setPendientesCount(res.data.data.pendientes_count || 0);
    } catch (error) {
      toast.error('Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async () => {
    setProcessing(true);
    try {
      await pagosPropietarioService.confirmar(selected.id);
      toast.success('Pago confirmado correctamente');
      setConfirmAction(null);
      setSelected(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al confirmar');
    } finally {
      setProcessing(false);
    }
  };

  const handleRechazar = async () => {
    if (!motivoRechazo.trim()) {
      toast.error('Ingresa el motivo del rechazo');
      return;
    }
    
    setProcessing(true);
    try {
      await pagosPropietarioService.rechazar(selected.id, { motivo: motivoRechazo });
      toast.success('Pago rechazado');
      setModalRechazar(false);
      setMotivoRechazo('');
      setSelected(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al rechazar');
    } finally {
      setProcessing(false);
    }
  };

  const getEstadoVariant = (estado) => {
    const variants = { PENDIENTE: 'warning', CONFIRMADO: 'success', RECHAZADO: 'danger' };
    return variants[estado] || 'default';
  };

  const columns = [
    {
      header: 'Fecha',
      render: (row) => (
        <div>
          <p className="text-white">{formatDate(row.fecha_pago)}</p>
          <p className="text-xs text-slate-500">Reg: {formatDate(row.created_at)}</p>
        </div>
      )
    },
    {
      header: 'Departamento',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-slate-700">
            <Building size={16} className="text-slate-400" />
          </div>
          <div>
            <p className="font-medium text-white">Dpto {row.departamento_numero}</p>
            <p className="text-xs text-slate-500">{row.departamento_propietario}</p>
          </div>
        </div>
      )
    },
    {
      header: 'Operación',
      render: (row) => (
        <div>
          <p className="font-mono text-white">{row.numero_operacion}</p>
          <p className="text-xs text-slate-500">{row.banco || 'Sin banco'}</p>
        </div>
      )
    },
    {
      header: 'Recibos',
      render: (row) => (
        <div>
          <p className="text-white">{row.recibos?.length || 0} recibo(s)</p>
          <p className="text-xs text-slate-500 truncate max-w-[150px]">
            {row.recibos?.map(r => `${r.mes}/${r.anio}`).join(', ')}
          </p>
        </div>
      )
    },
    {
      header: 'Monto',
      render: (row) => (
        <span className="text-lg font-bold text-emerald-400">{formatMoney(row.monto_total)}</span>
      )
    },
    {
      header: 'Estado',
      render: (row) => (
        <Badge variant={getEstadoVariant(row.estado)}>{row.estado}</Badge>
      )
    },
    {
      header: 'Acciones',
      render: (row) => (
        <div className="flex gap-1">
          <button
            onClick={() => { setSelected(row); setModalDetalle(true); }}
            className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white"
            title="Ver detalle"
          >
            <Eye size={16} />
          </button>
          {row.estado === 'PENDIENTE' && (
            <>
              <button
                onClick={() => { setSelected(row); setConfirmAction('confirmar'); }}
                className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-emerald-400"
                title="Confirmar pago"
              >
                <CheckCircle size={16} />
              </button>
              <button
                onClick={() => { setSelected(row); setModalRechazar(true); }}
                className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400"
                title="Rechazar pago"
              >
                <XCircle size={16} />
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Confirmar Pagos</h1>
          <p className="text-slate-400">Pagos de propietarios pendientes de confirmación</p>
        </div>
        {pendientesCount > 0 && (
          <div className="px-4 py-2 rounded-xl bg-amber-500/20 border border-amber-500/30">
            <span className="text-amber-400 font-medium">
              {pendientesCount} pago(s) pendiente(s)
            </span>
          </div>
        )}
      </div>

      {/* Stats rápidos */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-500/20">
              <Clock className="text-amber-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Pendientes</p>
              <p className="text-2xl font-bold text-amber-400">{pendientesCount}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-500/20">
              <CheckCircle className="text-emerald-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Confirmados Hoy</p>
              <p className="text-2xl font-bold text-emerald-400">
                {pagos.filter(p => p.estado === 'CONFIRMADO' && 
                  new Date(p.fecha_confirmacion).toDateString() === new Date().toDateString()
                ).length}
              </p>
            </div>
          </div>
        </Card>
        
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-500/20">
              <DollarSign className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Monto Pendiente</p>
              <p className="text-2xl font-bold text-white">
                {formatMoney(pagos.filter(p => p.estado === 'PENDIENTE')
                  .reduce((sum, p) => sum + parseFloat(p.monto_total), 0))}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="p-4">
        <div className="flex gap-4">
          <Select
            label="Estado"
            value={filters.estado}
            onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
            options={[
              { value: 'PENDIENTE', label: 'Pendientes' },
              { value: 'CONFIRMADO', label: 'Confirmados' },
              { value: 'RECHAZADO', label: 'Rechazados' },
              { value: '', label: 'Todos' }
            ]}
            className="w-48"
          />
        </div>
      </Card>

      {/* Tabla */}
      <Card className="overflow-hidden">
        <Table 
          columns={columns} 
          data={pagos} 
          loading={loading} 
          emptyMessage="No hay pagos para mostrar"
        />
      </Card>

      {/* Modal Detalle */}
      <Modal
        isOpen={modalDetalle}
        onClose={() => setModalDetalle(false)}
        title="Detalle del Pago"
        size="lg"
      >
        {selected && (
          <div className="space-y-4">
            {/* Encabezado */}
            <div className="p-4 rounded-xl bg-slate-700/50 flex items-center justify-between">
              <div>
                <p className="text-slate-400">Departamento</p>
                <p className="text-xl font-bold text-white">Dpto {selected.departamento_numero}</p>
                <p className="text-sm text-slate-400">{selected.departamento_propietario}</p>
              </div>
              <div className="text-right">
                <p className="text-slate-400">Monto Total</p>
                <p className="text-2xl font-bold text-emerald-400">{formatMoney(selected.monto_total)}</p>
                <Badge variant={getEstadoVariant(selected.estado)}>{selected.estado}</Badge>
              </div>
            </div>

            {/* Datos del pago */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CreditCard size={16} className="text-slate-500" />
                <div>
                  <p className="text-slate-500">N° Operación</p>
                  <p className="text-white font-mono">{selected.numero_operacion}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Building size={16} className="text-slate-500" />
                <div>
                  <p className="text-slate-500">Banco</p>
                  <p className="text-white">{selected.banco || 'No especificado'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-slate-500" />
                <div>
                  <p className="text-slate-500">Fecha de Pago</p>
                  <p className="text-white">{formatDate(selected.fecha_pago)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <User size={16} className="text-slate-500" />
                <div>
                  <p className="text-slate-500">Registrado por</p>
                  <p className="text-white">{selected.usuario_nombre}</p>
                </div>
              </div>
            </div>

            {/* Recibos incluidos */}
            <div>
              <p className="text-sm text-slate-400 mb-2">Recibos incluidos:</p>
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-700/50">
                    <tr>
                      <th className="px-3 py-2 text-left text-slate-400">Concepto</th>
                      <th className="px-3 py-2 text-center text-slate-400">Período</th>
                      <th className="px-3 py-2 text-right text-slate-400">Monto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {selected.recibos?.map((recibo, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 text-white">{recibo.concepto}</td>
                        <td className="px-3 py-2 text-center text-slate-400">{recibo.mes}/{recibo.anio}</td>
                        <td className="px-3 py-2 text-right text-white">{formatMoney(recibo.monto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {selected.observaciones && (
              <div>
                <p className="text-sm text-slate-400">Observaciones:</p>
                <p className="text-slate-300 mt-1">{selected.observaciones}</p>
              </div>
            )}

            {selected.estado === 'CONFIRMADO' && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-sm text-emerald-300">
                  ✓ Confirmado el {formatDate(selected.fecha_confirmacion)} por {selected.confirmado_por_nombre}
                </p>
              </div>
            )}

            {selected.estado === 'RECHAZADO' && (
              <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30">
                <p className="text-sm text-rose-300">
                  ✗ Rechazado: {selected.motivo_rechazo}
                </p>
              </div>
            )}

            {/* Acciones si está pendiente */}
            {selected.estado === 'PENDIENTE' && (
              <div className="flex gap-3 pt-4 border-t border-slate-700">
                <Button 
                  variant="success" 
                  className="flex-1"
                  icon={CheckCircle}
                  onClick={() => { setModalDetalle(false); setConfirmAction('confirmar'); }}
                >
                  Confirmar Pago
                </Button>
                <Button 
                  variant="danger" 
                  className="flex-1"
                  icon={XCircle}
                  onClick={() => { setModalDetalle(false); setModalRechazar(true); }}
                >
                  Rechazar
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal Rechazar */}
      <Modal
        isOpen={modalRechazar}
        onClose={() => { setModalRechazar(false); setMotivoRechazo(''); }}
        title="Rechazar Pago"
      >
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-rose-400 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-rose-300">¿Estás seguro de rechazar este pago?</p>
                <p className="text-sm text-slate-400 mt-1">
                  Pago de {selected?.departamento_numero} por {formatMoney(selected?.monto_total)}
                </p>
              </div>
            </div>
          </div>

          <Textarea
            label="Motivo del Rechazo *"
            value={motivoRechazo}
            onChange={(e) => setMotivoRechazo(e.target.value)}
            placeholder="Explica el motivo del rechazo (número de operación incorrecto, monto no coincide, etc.)"
            rows={3}
            required
          />

          <div className="flex justify-end gap-3">
            <Button 
              variant="secondary" 
              onClick={() => { setModalRechazar(false); setMotivoRechazo(''); }}
            >
              Cancelar
            </Button>
            <Button 
              variant="danger" 
              onClick={handleRechazar}
              disabled={processing}
            >
              {processing ? 'Rechazando...' : 'Rechazar Pago'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirm Aprobar */}
      <ConfirmDialog
        isOpen={confirmAction === 'confirmar'}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirmar}
        title="Confirmar Pago"
        message={`¿Confirmar el pago de ${selected?.departamento_numero} por ${formatMoney(selected?.monto_total)}? 
        
Esto registrará el ingreso y marcará los recibos como pagados.`}
        confirmText={processing ? 'Confirmando...' : 'Confirmar Pago'}
      />
    </div>
  );
};

export default ConfirmarPagosPage;
